import { useEffect, useMemo, useState } from 'react';
import { View, Pressable, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import type { Expense, ExpenseCategory } from '@/services/expenses';
import {
  expenseListHasForeignCurrency,
  formatCurrency,
  sumExpensesInTripBaseCurrency,
  type CurrencyCode,
} from '@/utils/currency';
import { coerceDate } from '@/utils/dateKeys';
import { DualCurrencyDisplay } from './DualCurrencyDisplay';

type ExpenseListProps = {
  expenses: Expense[];
  onEditExpense?: (expense: Expense) => void;
  onDeleteExpense?: (expense: Expense) => void;
  onViewExpense?: (expense: Expense) => void; // New prop for viewing expense details
  canEdit?: boolean;
  canEditExpense?: (expense: Expense) => boolean; // Function to check if specific expense can be edited
  noCard?: boolean; // If true, render without Card wrapper
  baseCurrency?: CurrencyCode; // Base currency for conversion display
  allowLiveConversion?: boolean; // Toggle live conversion to avoid list jank
  tripMembers?: Array<{ userId: string; name?: string; email: string }>; // For split details
  currentUserId?: string; // For split details
};

const getCategoryIcon = (category: ExpenseCategory): keyof typeof Ionicons.glyphMap => {
  switch (category) {
    case 'flight':
      return 'airplane-outline';
    case 'lodging':
      return 'bed-outline';
    case 'food':
      return 'restaurant-outline';
    case 'transport':
      return 'car-outline';
    case 'activity':
      return 'ticket-outline';
    case 'shopping':
      return 'bag-outline';
    default:
      return 'receipt-outline';
  }
};

const getCategoryColor = (category: ExpenseCategory, theme: any): string => {
  // You can customize colors per category
  return theme.colors.primary;
};

export function ExpenseList({
  expenses,
  onEditExpense,
  onDeleteExpense,
  onViewExpense,
  canEdit = true,
  canEditExpense,
  noCard = false,
  baseCurrency = 'USD',
  allowLiveConversion = true,
  tripMembers = [],
  currentUserId,
}: ExpenseListProps) {
  const theme = useTheme();
  const [visibleCount, setVisibleCount] = useState(30);
  const [totalInBaseCurrency, setTotalInBaseCurrency] = useState<number | null>(null);
  const [totalConverting, setTotalConverting] = useState(false);

  useEffect(() => {
    // Reset progressive rendering window when backing data changes.
    setVisibleCount(30);
  }, [expenses]);

  useEffect(() => {
    if (expenses.length === 0) {
      setTotalInBaseCurrency(null);
      setTotalConverting(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      if (!expenseListHasForeignCurrency(expenses, baseCurrency)) {
        const total = expenses.reduce((sum, exp) => sum + exp.amount, 0);
        if (!cancelled) {
          setTotalInBaseCurrency(total);
          setTotalConverting(false);
        }
        return;
      }
      setTotalConverting(true);
      setTotalInBaseCurrency(null);
      try {
        const total = await sumExpensesInTripBaseCurrency(expenses, baseCurrency);
        if (!cancelled) {
          setTotalInBaseCurrency(total);
        }
      } catch (error) {
        console.error('[ExpenseList] Failed to sum expenses in base currency', error);
        if (!cancelled) {
          setTotalInBaseCurrency(null);
        }
      } finally {
        if (!cancelled) {
          setTotalConverting(false);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [expenses, baseCurrency]);

  const visibleExpenses = useMemo(
    () => expenses.slice(0, Math.min(visibleCount, expenses.length)),
    [expenses, visibleCount],
  );

  if (expenses.length === 0) {
    const emptyContent = (
      <View style={{ alignItems: 'center', paddingVertical: theme.spacing(3), gap: theme.spacing(1) }}>
        <Ionicons name="receipt-outline" size={32} color={theme.colors.textSecondary} />
        <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
          No expenses yet. Add your first expense to start tracking spending.
        </Body>
      </View>
    );
    if (noCard) return emptyContent;
    return (
      <Card variant="outlined">
        {emptyContent}
      </Card>
    );
  }

  const groupedVisibleExpenses = useMemo(() => {
    const expensesByDate = new Map<string, Expense[]>();
    visibleExpenses.forEach(expense => {
      const safeDate = coerceDate(expense.date);
      const dateKey = safeDate
        ? safeDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : 'Date TBD';
      if (!expensesByDate.has(dateKey)) {
        expensesByDate.set(dateKey, []);
      }
      expensesByDate.get(dateKey)!.push(expense);
    });
    return Array.from(expensesByDate.entries());
  }, [visibleExpenses]);

  const content = (
    <>
      <View style={{ gap: theme.spacing(2), marginTop: theme.spacing(1) }}>
        {groupedVisibleExpenses.map(([dateKey, dateExpenses]) => (
          <View key={dateKey} style={{ gap: theme.spacing(1.5) }}>
            <Caption color={theme.colors.textSecondary} style={{ fontWeight: '600', marginTop: theme.spacing(0.5) }}>
              {dateKey}
            </Caption>
            {dateExpenses.map((expense) => {
              const categoryIcon = getCategoryIcon(expense.category);
              const categoryColor = getCategoryColor(expense.category, theme);

              return (
                <Pressable
                  key={expense.id}
                  onPress={() => onViewExpense?.(expense)}
                  style={{
                    flexDirection: 'row',
                    gap: theme.spacing(2),
                    paddingVertical: theme.spacing(1),
                    borderBottomWidth: 1,
                    borderBottomColor: theme.colors.borderMuted,
                    opacity: 1,
                  }}
                >
                  <View
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: theme.radii.md,
                      backgroundColor: categoryColor + '20',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Ionicons name={categoryIcon} size={20} color={categoryColor} />
                  </View>
                  <View style={{ flex: 1, gap: theme.spacing(0.25) }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                          <ThemedText variant="bodyStrong" style={{ fontSize: 15 }}>
                            {expense.description || (expense.status === 'pending' ? 'Processing receipt...' : 'Expense')}
                          </ThemedText>
                          {expense.status === 'pending' && (
                            <View style={{
                              backgroundColor: theme.colors.warning + '20',
                              paddingHorizontal: theme.spacing(0.75),
                              paddingVertical: theme.spacing(0.25),
                              borderRadius: theme.radii.pill,
                            }}>
                              <Caption color={theme.colors.warning} style={{ fontSize: 9, fontWeight: '600' }}>
                                OCR
                              </Caption>
                            </View>
                          )}
                          {expense.status === 'failed' && (
                            <View style={{
                              backgroundColor: theme.colors.error + '20',
                              paddingHorizontal: theme.spacing(0.75),
                              paddingVertical: theme.spacing(0.25),
                              borderRadius: theme.radii.pill,
                            }}>
                              <Caption color={theme.colors.error} style={{ fontSize: 9, fontWeight: '600' }}>
                                Failed
                              </Caption>
                            </View>
                          )}
                        </View>
                        {expense.merchant && (
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                            {expense.merchant}
                          </Caption>
                        )}
                        {expense.location && (
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                            {expense.location}
                          </Caption>
                        )}
                      </View>
                      <View style={{ alignItems: 'flex-end' }}>
                        <DualCurrencyDisplay
                          amount={expense.amount}
                          currency={expense.currency || 'USD'}
                          baseCurrency={baseCurrency}
                          size="medium"
                          allowLiveConversion={allowLiveConversion}
                          savedConversion={(expense as any).conversion}
                        />
                        {expense.paymentMethod && (
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, marginTop: theme.spacing(0.25) }}>
                            {expense.paymentMethod}
                          </Caption>
                        )}
                      </View>
                    </View>
                  </View>
                  {(() => {
                    const expenseCanEdit = canEditExpense ? canEditExpense(expense) : canEdit;
                    if (!expenseCanEdit || !(onEditExpense || onDeleteExpense)) {
                      return null;
                    }
                    return (
                      <View style={{ flexDirection: 'row', gap: theme.spacing(0.5) }}>
                        {onEditExpense && (
                          <Pressable
                            onPress={(e) => {
                              e.stopPropagation();
                              onEditExpense(expense);
                            }}
                            style={({ pressed }) => ({
                              padding: theme.spacing(0.5),
                              opacity: pressed ? 0.7 : 1,
                            })}
                          >
                            <Ionicons name="pencil-outline" size={18} color={theme.colors.textSecondary} />
                          </Pressable>
                        )}
                        {onDeleteExpense && (
                          <Pressable
                            onPress={(e) => {
                              e.stopPropagation();
                              onDeleteExpense(expense);
                            }}
                            style={({ pressed }) => ({
                              padding: theme.spacing(0.5),
                              opacity: pressed ? 0.7 : 1,
                            })}
                          >
                            <Ionicons name="trash-outline" size={18} color={theme.colors.error} />
                          </Pressable>
                        )}
                      </View>
                    );
                  })()}
                </Pressable>
              );
            })}
          </View>
        ))}
      </View>
      {visibleExpenses.length < expenses.length && (
        <Pressable
          onPress={() => setVisibleCount((count) => Math.min(count + 30, expenses.length))}
          style={({ pressed }) => ({
            marginTop: theme.spacing(1.5),
            alignSelf: 'center',
            paddingHorizontal: theme.spacing(2),
            paddingVertical: theme.spacing(1),
            borderRadius: theme.radii.md,
            borderWidth: 1,
            borderColor: theme.colors.borderMuted,
            opacity: pressed ? 0.92 : 1,
          })}
          accessibilityRole="button"
          accessibilityLabel="Load more expenses"
          accessibilityHint="Loads more items in the expense list"
        >
          <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
            Show more ({expenses.length - visibleExpenses.length} remaining)
          </Caption>
        </Pressable>
      )}
      {expenses.length > 0 && (
        <View style={{ marginTop: theme.spacing(2), paddingTop: theme.spacing(1.5), borderTopWidth: 1, borderTopColor: theme.colors.borderMuted }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <ThemedText variant="bodyStrong">Total</ThemedText>
            {totalConverting ? (
              <ActivityIndicator size="small" color={theme.colors.primary} accessibilityLabel="Converting totals" />
            ) : (
              <ThemedText variant="bodyStrong" style={{ fontSize: 18 }}>
                {totalInBaseCurrency == null
                  ? '—'
                  : formatCurrency(totalInBaseCurrency, baseCurrency)}
              </ThemedText>
            )}
          </View>
        </View>
      )}
    </>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card variant="outlined">
      <SubHeading>Expenses</SubHeading>
      {content}
    </Card>
  );
}

